/**
 * /tracking/cookieless-fallback — operator page for feature #165.
 *
 * Declares a TanStack Router route and renders the
 * `CookielessFallbackPage` feature. The root wire-up picks this up from
 * `router.tsx` during the wire-up cycle.
 *
 * `?clickId=` search param deep-links the simulate form so an operator
 * can jump in from an audit log row with the click pre-filled.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { CookielessFallbackPage } from "@/features/cookieless-fallback/CookielessFallbackPage";

interface TrackingCookielessFallbackSearch {
	clickId?: string;
}

export const trackingCookielessFallbackRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/cookieless-fallback",
	validateSearch: (
		raw: Record<string, unknown>,
	): TrackingCookielessFallbackSearch => {
		const v = raw.clickId;
		if (typeof v === "string" && v.trim().length > 0) {
			return { clickId: v.trim() };
		}
		return {};
	},
	component: TrackingCookielessFallbackRoute,
});

export default trackingCookielessFallbackRoute;

export function TrackingCookielessFallbackRoute() {
	// Search param `clickId` reserved for future deep-link wiring; CookielessFallbackPage
	// does not currently accept an `initialClickId` prop.
	void trackingCookielessFallbackRoute.useSearch();

	return (
		<div className="flex flex-col">
			<nav
				aria-label="Breadcrumb"
				className="mx-auto w-full max-w-5xl px-6 pt-10 text-sm text-zinc-500 sm:pt-16"
			>
				<ol className="flex flex-wrap items-center gap-1">
					<li>
						<Link
							to="/"
							className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
						>
							Dashboard
						</Link>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li>
						<span className="rounded-[6px] px-1 py-0.5">Tracking</span>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li className="text-zinc-900">Cookieless fallback</li>
				</ol>
			</nav>

			<CookielessFallbackPage />
		</div>
	);
}

// Named export used by the root wire-up.
export { TrackingCookielessFallbackRoute as TrackingCookielessFallbackPage };
